import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { FilterTypesEnum } from 'src/app/models/filters/filterTypes.enum';
import { ViolationsFiltersModel } from 'src/app/models/filters/implementations/pageFilters/violationsFilters.model';
import { ISelectOption } from 'src/app/models/selectOptions';
import { ViolationsCustomViewOptions } from 'src/app/models/selectOptions/violationsCustomViewOptions.model';
import { ViolationsCustomViewKey } from 'src/app/models/constants/violationsCustomView.constants';
import { FiltersQueryTransformer } from 'src/app/services/filters/filtersQueryTransformer.ts/filtersQueryTransformer';
import { AppValidators } from 'src/app/common/appValidators';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';

@Component({
  selector: 'app-violations-filters',
  templateUrl: './violations-filters.component.html',
  styleUrls: ['./violations-filters.component.scss'],
})
export class ViolationsFiltersComponent implements OnInit {
  @Input() public pageFilters: ViolationsFiltersModel;

  public clearCustomViewSelection$ = new BehaviorSubject(undefined);

  public selectedOptions: ISelectOption[] = [];

  public searchBoxNewValue = new ReplaySubject<string | undefined>(1);
  public fromDateNewValue = new ReplaySubject<Date | undefined>(1);
  public toDateNewValue = new ReplaySubject<Date | undefined>(1);
  public customViewOptions: ISelectOption[] = ViolationsCustomViewOptions;
  public selectedCustomOption?: ISelectOption;

  public searchValidators = [AppValidators.evenNumberOfQuoteMarks];

  private _customViewTriggered = false;

  constructor(private readonly _router: Router) {}

  ngOnInit(): void {
    this.pageFilters.onUpdate$.subscribe((data) => {
      this.selectedOptions = this.pageFilters.getAllSelectedOptions();

      this.searchBoxNewValue.next(data.search);
      this.fromDateNewValue.next(data.fromDate);
      this.toDateNewValue.next(data.toDate);

      if (this._customViewTriggered) {
        this._customViewTriggered = false;
      } else {
        this.clearCustomViewSelection$.next(undefined);
        this.selectedCustomOption = undefined;
      }
    });
  }

  public tableCustomViewChange(selectedOption: ISelectOption) {
    this.selectedCustomOption = selectedOption;
    this._customViewTriggered = true;
    this._updateFiltersByCustomView(selectedOption);
  }

  public fromDateSelected(value?: Date): void {
    this.pageFilters.updateFilterFromUserSelection(FilterTypesEnum.FromDate, value);
  }

  public toDateSelected(value?: Date): void {
    this.pageFilters.updateFilterFromUserSelection(FilterTypesEnum.ToDate, value);
  }

  public searchSubmitted(value?: string): void {
    this.pageFilters.updateFilterFromUserSelection(FilterTypesEnum.Search, value);
  }

  private _updateFiltersByCustomView(selectedView: ISelectOption) {
    let violationsFiltersByKeyValue: ISelectedFilters = {};
    switch (selectedView.id) {
      case ViolationsCustomViewKey.overSharedResources:
        violationsFiltersByKeyValue = {
          violationRule: ['ZN_P00048', 'ZN_P00046'],
        };
        break;
      case ViolationsCustomViewKey.untrustedGuestAccess:
        violationsFiltersByKeyValue = {
          violationRule: ['ZN_P00200', 'ZN_P00201', 'ZN_P00202', 'ZN_P00203', 'ZN_P00204', 'ZN_P00205', 'ZN_P00212'],
        };
        break;
      case ViolationsCustomViewKey.untrustedMemberAccess:
        violationsFiltersByKeyValue = {
          violationRule: ['ZN_P00206', 'ZN_P00207', 'ZN_P00208', 'ZN_P00209', 'ZN_P00210', 'ZN_P00211', 'ZN_P00214'],
        };
        break;
      case ViolationsCustomViewKey.nonCompliantConnections:
        violationsFiltersByKeyValue = { violationRule: ['ZN_P00105'] };
        break;
      case ViolationsCustomViewKey.insecureCommunication:
        violationsFiltersByKeyValue = {
          violationRule: ['ZN_P00032', 'ZN_P00080'],
        };
        break;
      case ViolationsCustomViewKey.authenticationIssues:
        violationsFiltersByKeyValue = {
          violationRule: ['ZN_P00058', 'ZN_P00080'],
        };
        break;
      case ViolationsCustomViewKey.customCode:
        violationsFiltersByKeyValue = {
          violationRule: ['ZN_P00035'],
        };
        break;
      case ViolationsCustomViewKey.potentiallyRiskyDataFlow:
        violationsFiltersByKeyValue = {
          violationRule: [
            'ZN_P00015',
            'ZN_P00013',
            'ZN_P00041',
            'ZN_P00014',
            'ZN_P00036',
            'ZN_P00037',
            'ZN_P00038',
            'ZN_P00039',
            'ZN_P00040',
          ],
        };
        break;
      case ViolationsCustomViewKey.suspiciousConfiguration:
        violationsFiltersByKeyValue = {
          violationRule: ['ZN_P00114', 'ZN_P00025'],
        };
        break;
    }
    violationsFiltersByKeyValue.violationStatus = ['open'];
    violationsFiltersByKeyValue.resourceStatus = ['active'];

    const filtersQuery = FiltersQueryTransformer.transformToQuery(violationsFiltersByKeyValue);
    this._router.navigate([], { queryParams: { q: filtersQuery } });
  }
}
